import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { countryCodes } from '../../utils/constants';

export interface CountryCode {
  country: string;
  code: string;
  flag: string;
}

interface CountryDropdownProps {
  selectedCountry: CountryCode;
  onSelect: (country: CountryCode) => void;
  disabled?: boolean;
  className?: string;
  'data-testid'?: string;
}

const CountryDropdown: React.FC<CountryDropdownProps> = ({
  selectedCountry,
  onSelect,
  disabled = false,
  className = '',
  'data-testid': dataTestId
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearch('');
      }
    }
  };

  const handleCountrySelect = (country: CountryCode) => {
    onSelect(country);
    setIsOpen(false);
    setSearch('');
  };

  const filteredCountries = countryCodes.filter(country => 
    country.country.toLowerCase().includes(search.toLowerCase()) ||
    country.code.includes(search)
  );

  const baseTestId = dataTestId || 'country-dropdown';

  return (
    <div className={`relative w-40 ${className}`} ref={dropdownRef} data-testid={baseTestId}>
      <button
        type="button"
        className={`country-dropdown-trigger flex items-center justify-between w-full px-3 py-2 text-sm text-left bg-input border border-border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary ${
          disabled ? 'bg-secondary text-foreground-muted cursor-not-allowed opacity-70' : 'hover:bg-accent'
        }`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        data-testid={`${baseTestId}-trigger`}
      >        <span className="flex items-center">
          <span className="text-xl mr-2">{selectedCountry.flag}</span>
          <span>{selectedCountry.code}</span>
        </span>
        <ChevronDown className={`h-4 w-4 text-foreground-muted transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-64 bg-background-paper rounded-lg shadow-lg border border-border overflow-hidden" data-testid={`${baseTestId}-list`}>
          <div className="p-2 border-b border-border">
            <div className="relative">
              <input
                type="text"
                placeholder="Search countries..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className="form-input w-full pl-8 pr-10 py-2 border border-border rounded-lg bg-input text-foreground placeholder:text-foreground-muted"
                autoFocus
                data-testid={`${baseTestId}-search-input`}
              />
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-foreground-muted" />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-2.5 text-foreground-muted hover:text-foreground"
                  data-testid={`${baseTestId}-clear-search-button`}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
          <div className="max-h-60 overflow-y-auto" data-testid={`${baseTestId}-options`}>
            {filteredCountries.map(country => (
              <div
                key={country.code}
                className="flex items-center space-x-2 p-2 hover:bg-accent cursor-pointer text-foreground"
                onClick={() => handleCountrySelect(country)}
                data-testid={`${baseTestId}-option-${country.code.replace('+', '')}`}
              >
                <span className="text-xl">{country.flag}</span>
                <span>{country.code}</span>
                <span className="text-sm">{country.country}</span>
              </div>
            ))}
            {filteredCountries.length === 0 && (
              <div className="p-2 text-foreground-muted text-center" data-testid={`${baseTestId}-no-results`}>No countries found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CountryDropdown;